/**
 * =============================================================================
 * SCRIPT-NAME:   HaikuRecommendations.js
 * MODUL:         Ads Performance Dashboard – Haiku-Empfehlungs-Liste
 * BESCHREIBUNG:  Zeigt die letzten 12 Haiku-Empfehlungen, Status pro
 *                Empfehlung änderbar (open/applied/skipped).
 * VERSION:       v1.0
 * DATUM:         2026-06-09
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import './HaikuRecommendations.css';

const API_GET    = '/api/ads_recommendations.php';
const API_UPDATE = '/api/ads_recommendations.php';

const STATUS_LABEL = {
    open:    { label: 'offen',     icon: '⏳', cls: 'rec-status-open' },
    applied: { label: 'erledigt',  icon: '✅', cls: 'rec-status-applied' },
    skipped: { label: 'verworfen', icon: '🗑️', cls: 'rec-status-skipped' },
};

export default function HaikuRecommendations({ limit = 12 }) {
    const [recs,    setRecs]    = useState([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);
    const [filter,  setFilter]  = useState('open');

    const load = useCallback(() => {
        const token = localStorage.getItem('token') || '';
        setLoading(true);
        setError(null);
        const url = filter ? `${API_GET}?limit=${limit}&status=${filter}` : `${API_GET}?limit=${limit}`;
        fetch(url, { headers: { 'Authorization': 'Bearer ' + token } })
            .then(r => r.json())
            .then(d => {
                if (d && d.success) setRecs(d.data || []);
                else setError(d.message || 'Fehler');
            })
            .catch(e => setError(String(e)))
            .finally(() => setLoading(false));
    }, [limit, filter]);

    useEffect(() => { load(); }, [load]);

    async function setStatus(id, status) {
        const token = localStorage.getItem('token') || '';
        try {
            const r = await fetch(API_UPDATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ id, status }),
            });
            const d = await r.json();
            if (!d.success) throw new Error(d.message || 'Update fehlgeschlagen');
            load();
        } catch (e) {
            setError(String(e));
        }
    }

    function fmtDate(s) {
        if (!s) return '';
        return s.substring(0, 10);
    }

    return (
        <div className="haiku-recs">
            <div className="haiku-recs-toolbar">
                <span className="haiku-recs-filter-label">Status:</span>
                {['open', 'applied', 'skipped', ''].map(s => (
                    <button
                        key={s || 'alle'}
                        className={'haiku-recs-filter-btn' + (filter === s ? ' active' : '')}
                        onClick={() => setFilter(s)}
                    >
                        {s ? STATUS_LABEL[s].icon + ' ' + STATUS_LABEL[s].label : 'alle'}
                    </button>
                ))}
                <button className="haiku-recs-reload" onClick={load} title="Neu laden">↻</button>
            </div>

            {loading && <p className="haiku-recs-info">Lädt …</p>}
            {error && <p className="haiku-recs-error">⚠ {error}</p>}
            {!loading && !error && recs.length === 0 && (
                <p className="haiku-recs-info">Keine Empfehlungen für diesen Filter.</p>
            )}

            {recs.map(rec => {
                const st = STATUS_LABEL[rec.status] || STATUS_LABEL.open;
                return (
                    <div key={rec.id} className={'haiku-rec-card ' + st.cls}>
                        <div className="haiku-rec-head">
                            <span className="haiku-rec-rank">#{rec.rank_no}</span>
                            {rec.lp_zuordnung && <span className="haiku-rec-lp">{rec.lp_zuordnung}</span>}
                            <span className="haiku-rec-date">{fmtDate(rec.created_at)} · {rec.week_label}</span>
                            <span className={'haiku-rec-status ' + st.cls}>{st.icon} {st.label}</span>
                        </div>
                        <div className="haiku-rec-title">{rec.title}</div>
                        {rec.body && <div className="haiku-rec-body">{rec.body}</div>}
                        <div className="haiku-rec-actions">
                            {rec.status !== 'applied' && (
                                <button className="haiku-rec-btn haiku-rec-btn-applied" onClick={() => setStatus(rec.id, 'applied')}>
                                    ✅ Erledigt
                                </button>
                            )}
                            {rec.status !== 'skipped' && (
                                <button className="haiku-rec-btn haiku-rec-btn-skipped" onClick={() => setStatus(rec.id, 'skipped')}>
                                    🗑️ Verwerfen
                                </button>
                            )}
                            {rec.status !== 'open' && (
                                <button className="haiku-rec-btn haiku-rec-btn-open" onClick={() => setStatus(rec.id, 'open')}>
                                    ⏳ Wieder öffnen
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
